import type { BlockRow } from './types.js';

/**
 * W-4 (Sol M-03 residue) — the round-trip byte invariant and the restore caps
 * derived from it, in ONE module so import, export and restore stay in
 * lock-step. This file pulls in NO zip machinery (jszip), so wiring the import
 * invariant into the admission path costs the import bundle nothing.
 *
 * THE FINDING: nothing bounded persisted TEXT bytes. The 50 MB file gate and
 * the 2 M word gate both accept a 50 MiB single-"word" file of C0 control
 * characters — control chars are not whitespace, so it is one word, and its raw
 * bytes are under 50 MB. JSON.stringify escapes each such char to `\uXXXX`
 * (6 bytes per source byte), so that document's blocks entry serializes to
 * >300 MB. It EXPORTS fine (export had no byte gate) but can never be RESTORED:
 * >300 MB exceeds the restore per-entry cap. The fix bounds the persisted
 * document at IMPORT so its serialized blocks entry provably fits the caps, and
 * derives the caps from that bound.
 *
 * MECHANISM (a) — FORMAT-HONEST. We measure the ACTUAL serialized blocks JSON
 * byte length (exactly `JSON.stringify(rows, null, 1)`, the same call export
 * makes) and gate on it. No expansion-factor arithmetic to get wrong: escaping
 * AND per-row framing are captured by construction. A cheap conservative floor
 * runs first so a hostile unbounded paste is rejected before the honest
 * measurement ever allocates.
 *
 * JOINTLY-DERIVED NUMBERS (worst case):
 *  - Import invariant B = 32 MiB: one document's serialized blocks entry ≤ B.
 *  - A maximal-legal 5-slot library therefore serializes blocks.json to < 5·B
 *    = 160 MiB. (Summing per-document measurements double-counts the outer
 *    `[ … ]` framing, so the single real array is strictly smaller than 5·B.)
 *  - Restore per-entry cap = 256 MiB:  160 · 1.5 = 240 ≤ 256  → ≥1.5× headroom.
 *  - Restore total cap = 512 MiB: the whole archive ≈ blocks.json + a few KB of
 *    the other seven stores ≈ 160 MiB;  160 · 1.5 = 240 ≤ 512 → ≥3.1× headroom.
 * The 256/512 restore caps are UNCHANGED (never lowered). B is the new
 * dimension that makes a maximal-legal library round-trip with headroom to
 * spare on BOTH bounds.
 */

/** Import invariant B: the byte ceiling on one document's serialized blocks. */
export const MAX_PERSISTED_BLOCKS_BYTES = 32 * 1024 * 1024;

/** Restore caps (also the export grandfather caps). Unchanged from Sol R1. */
export const RESTORE_MAX_ENTRY_BYTES = 256 * 1024 * 1024;
export const RESTORE_MAX_TOTAL_BYTES = 512 * 1024 * 1024;

/**
 * A safe LOWER bound on the bytes any single BlockRow serializes to under
 * `JSON.stringify(rows, null, 1)`. Every row carries a fixed 36-char docId —
 * `  "docId": "<36 chars>",` alone is ~51 bytes — plus the idx/kind/level/text
 * keys and braces. 50 is deliberately below the true minimum so the cheap floor
 * never over-rejects a legitimate document; it exists only to bound the
 * millions-of-tiny-blocks case before JSON.stringify is called.
 */
const MIN_SERIALIZED_ROW_BYTES = 50;

const IMPORT_REJECTION =
  'This document is too large to include in a backup — try splitting it into smaller documents.';

/**
 * UTF-8 byte length WITHOUT allocating the encoded bytes. `TextEncoder().encode`
 * would allocate a Uint8Array as large as the string just to read `.length`;
 * this is a pure O(n) scan over the UTF-16 code units.
 */
export function utf8ByteLength(str: string): number {
  let bytes = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < str.length) {
      // High surrogate that opens a pair → one 4-byte code point (skip the low
      // surrogate). A lone surrogate falls through to the 3-byte branch, which
      // matches how JSON.stringify emits it (\uXXXX is even longer, so this
      // stays a valid lower bound).
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

/**
 * The bytes a document's block rows contribute to blocks.json, measured EXACTLY
 * as exportBackup serializes the store (`JSON.stringify(rows, null, 1)`).
 */
export function serializedBlocksBytes(rows: readonly BlockRow[]): number {
  return utf8ByteLength(JSON.stringify(rows, null, 1));
}

/**
 * Typed, actionable rejection shared by the import invariant and the export
 * grandfather guard. Both surfaces render `error.message` (the import-failed
 * toast and the backup toast), so the message is written for the end user.
 */
export class BackupTooLargeError extends Error {
  /** The offending library document, when identifiable (export path). */
  readonly documentTitle?: string;

  constructor(message: string, documentTitle?: string) {
    super(message);
    this.name = 'BackupTooLargeError';
    if (documentTitle !== undefined) this.documentTitle = documentTitle;
  }
}

/**
 * W-4 import invariant, enforced on BOTH import paths (file + paste) BEFORE the
 * commit transaction opens — it can throw, and W-1 forbids throw-capable work
 * inside the readwrite txn. Mechanism (a), format-honest.
 */
export function assertPersistedBlocksBound(rows: readonly BlockRow[]): void {
  // Cheap conservative floor first — no large allocation. The serialized entry
  // is always ≥ Σ(escaped text) + rows·(min framing): escaping only grows text,
  // and every row carries a fixed docId + keys. This rejects an unbounded paste
  // (paste has no byte gate) and a millions-of-tiny-blocks input HERE, and caps
  // the honest measurement's transient allocation to ~6·B.
  let floor = rows.length * MIN_SERIALIZED_ROW_BYTES;
  if (floor > MAX_PERSISTED_BLOCKS_BYTES) throw new BackupTooLargeError(IMPORT_REJECTION);
  for (const row of rows) {
    floor += utf8ByteLength(row.text);
    if (floor > MAX_PERSISTED_BLOCKS_BYTES) throw new BackupTooLargeError(IMPORT_REJECTION);
  }
  // Format-honest measurement: the ACTUAL bytes this document adds to
  // blocks.json — escaping AND framing, no expansion-factor guesswork.
  if (serializedBlocksBytes(rows) > MAX_PERSISTED_BLOCKS_BYTES) {
    throw new BackupTooLargeError(IMPORT_REJECTION);
  }
}
